// =============================================================================
// domain/analyticsOverview.ts — the numbers behind the Analytics hero and tiles.
// -----------------------------------------------------------------------------
// The Overview sub-tab opens with one big figure and four tiles, each carrying a
// sparkline. Most of what they show already exists — `selectKpis` computes the
// P&L, the win rate, the profit factor and the drawdown, and `equityCurve` walks
// the cumulative curve. What is here is the rest:
//
//   averageR        the hero's "avg R"
//   dailyBuckets    trades per DAY, which is what the tile sparklines plot
//   runningPeak     the high-water line, and how far under it you are
//   trendVsPrior    the "vs prior period" pill
//
// ── ⚠ TWO SERIES, TWO GRANULARITIES, AND IT MATTERS ──
//
// The equity, peak and underwater sparklines are per TRADE — that is the curve
// the drawdown figure is computed against. Trade count and the running win rate
// are per DAY, because a per-day win rate over a one-trade day reads 0% or 100%
// and plots as noise. The old client makes the same split and says so.
//
// ── ⚠ NO WALL CLOCK, NO STORE (D26) ──
// The caller resolves each trade's effective date and hands it in.
// =============================================================================

export interface OverviewTrade {
  /** The EFFECTIVE date — resolved by the caller against the day-boundary mode. */
  date: string | null;
  netPnl: number | null;
  rrRealized: number | null;
}

// ---------------------------------------------------------------------------
// Average R
// ---------------------------------------------------------------------------

export interface AverageR {
  /**
   * ⚠ NULL WHEN NOTHING CARRIES AN R, never 0. The old client returns `0` here
   * and the hero prints `+0.00R avg R` — a measured-looking flat reading on a
   * book where no trade recorded a planned risk at all. "Break-even on average"
   * and "we could not measure this" are different facts and only one of them is
   * about the trader. PD-16.
   */
  avgR: number | null;
  /** ⚠ Its own population — trades carrying a realised R, not the period's count. */
  sample: number;
}

/**
 * Mean realised R across the trades that recorded one.
 *
 * ⚠ THE MEAN, not the median, and that is the old client's choice rather than an
 * oversight: beside a profit factor and a win rate, the figure a trader wants is
 * the average outcome per trade — the one that multiplies by trade count.
 */
export function averageR(trades: readonly OverviewTrade[]): AverageR {
  const rs: number[] = [];
  for (const t of trades) {
    // ⚠ `== null` FIRST. `Number(null)` is 0, so the finite check alone would
    // pull every trade with no recorded R into the mean as a scratch and drag
    // the average toward zero — on a book with few R-carrying trades, most of
    // the way to zero.
    if (t.rrRealized == null) continue;
    const r = Number(t.rrRealized);
    if (Number.isFinite(r)) rs.push(r);
  }
  return {
    avgR: rs.length ? rs.reduce((s, r) => s + r, 0) / rs.length : null,
    sample: rs.length,
  };
}

// ---------------------------------------------------------------------------
// Daily buckets
// ---------------------------------------------------------------------------

export interface DailyBucket {
  date: string;
  trades: number;
  wins: number;
  losses: number;
  netPnl: number;
}

/**
 * One bucket per day that has a trade, in date order.
 *
 * ⚠ DAYS WITH NO TRADES ARE ABSENT, not zero-filled. The tile beneath the
 * sparkline reads "51 trading days", and that number has to be the count of days
 * the trader actually traded — zero-filling the gaps would turn a 51-day count
 * into a 132-day one and make every bar a third of its height.
 */
export function dailyBuckets(trades: readonly OverviewTrade[]): DailyBucket[] {
  const by = new Map<string, DailyBucket>();
  for (const t of trades) {
    const d = t.date;
    if (!d) continue;
    const b = by.get(d) ?? { date: d, trades: 0, wins: 0, losses: 0, netPnl: 0 };
    const p = Number(t.netPnl) || 0;
    b.trades += 1;
    b.netPnl += p;
    if (p > 0) b.wins += 1;
    else if (p < 0) b.losses += 1;
    by.set(d, b);
  }
  return [...by.values()].sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * The win rate as it stood at the end of each trading day.
 *
 * ⚠ CUMULATIVE, and the old client's comment records why: a per-day win rate is
 * pure noise — a one-trade day is 0% or 100% — while the running curve actually
 * shows whether the rate is climbing or fading. It converges on the headline.
 *
 * ⚠ Scratches are in neither leg, so a day of nothing but flat trades leaves the
 * rate where it was rather than halving it.
 */
export function cumulativeWinRates(buckets: readonly DailyBucket[]): number[] {
  let w = 0; let l = 0;
  return buckets.map((b) => {
    w += b.wins;
    l += b.losses;
    return w + l ? (100 * w) / (w + l) : 0;
  });
}

// ---------------------------------------------------------------------------
// The high-water line
// ---------------------------------------------------------------------------

/**
 * The running maximum of a curve — its high-water mark at each point.
 *
 * ⚠ SEEDED AT THE FIRST VALUE, not at zero. Seeding at zero would draw a peak of
 * $0 across a book that opened underwater, which reads as "you were flat here"
 * on a stretch where the trader never was.
 */
export function runningPeak(values: readonly number[]): number[] {
  let max = -Infinity;
  return values.map((v) => {
    if (v > max) max = v;
    return max;
  });
}

/** How far below the high-water mark each point sits. ⚠ Always ≤ 0. */
export function underwater(values: readonly number[]): number[] {
  const peaks = runningPeak(values);
  return values.map((v, i) => {
    const d = v - (peaks[i] as number);
    // ⚠ Never NEGATIVE ZERO. `-0` survives JSON, fails a strict equality against
    // 0 and prints as "-$0" — this product has shipped that once already.
    return d === 0 ? 0 : d;
  });
}

/** The highest the cumulative curve ever reached. Null on an empty curve. */
export function peakEquity(values: readonly number[]): number | null {
  return values.length ? Math.max(...values) : null;
}

// ---------------------------------------------------------------------------
// The trend pill
// ---------------------------------------------------------------------------

/**
 * Percentage change against the previous period of the same length.
 *
 * ⚠ NULL IN THREE DIFFERENT SITUATIONS, all of which have to stay silent rather
 * than print a number:
 *   • the period has no fixed length (`All` has no "prior All");
 *   • nothing was traded in the prior window — no base to compare against;
 *   • the prior window came out exactly flat, and every change from zero is
 *     infinite.
 *
 * ⚠ THE DENOMINATOR IS THE ABSOLUTE PRIOR. Without it, improving from −$1,000 to
 * −$500 reports as −50% — the right direction rendered as the wrong sign, on the
 * one comparison a trader reads at a glance.
 */
export function trendVsPrior(periodPnl: number, priorPnl: number | null): number | null {
  if (priorPnl == null || priorPnl === 0) return null;
  return ((periodPnl - priorPnl) / Math.abs(priorPnl)) * 100;
}
